import { IFlagsConfig } from '@/models';
import { setFullPageLoader, updateFlagsConfig } from '@/store/reducers';
import { useDispatch, useStore } from 'react-redux';
import { RootState } from '@/store/store';
import { selectFlagsConfig } from '@/store/selectors';
import { applyPreference, isClickStreamOptedIn } from '@/utils/helpers/opt-in';
import {
  OPT_IN_PREFERENCE,
  OPT_IN_PREFERENCES,
  TERMS_UPDATED_CONSENT_BUCKETS,
  OptInPreference,
} from '@/constants/features/opt-in';
import { logger } from '@/utils/logger/logger';
import {
  CONSENT_CHECKBOXES,
  ConsentCheckboxId,
} from '@/features/dataConsent/config/checkboxes';
import { IConsentVariant } from '@/features/dataConsent/config/variants';
import { normalizePreferences } from '@/features/dataConsent/logic/bucket-grants';
import { checkboxesCountedAsAccepted } from '@/features/dataConsent/logic/stored-disclosures';
import {
  addGrantsToStoredBuckets,
  isMissingClickstreamConsent,
  newlyAcceptedDisclosureFlags,
} from '@/features/dataConsent/logic/consent-write';
import { CONSENT_WRITE_INVALID } from '@/constants/logger/logger-tags';
import { useUserConfig } from './useUserConfig.hook';

export const useFlagsConfig = () => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const userConfigHook = useUserConfig();

  const updateFlagConfig = async (
    payload: Partial<IFlagsConfig>
  ): Promise<void> => {
    dispatch(setFullPageLoader(true));
    try {
      await userConfigHook.updateFlagConfig(payload);
      dispatch(updateFlagsConfig(payload));
    } finally {
      dispatch(setFullPageLoader(false));
    }
  };

  // Optimistic Redux write with no full-page loader — for passive/non-critical UX flags.
  const updateFlagConfigSilently = async (
    payload: Partial<IFlagsConfig>
  ): Promise<void> => {
    await userConfigHook.updateFlagConfig(payload);
    dispatch(updateFlagsConfig(payload));
  };

  const updateSingleFlag = async <K extends keyof IFlagsConfig>(
    key: K,
    value: IFlagsConfig[K]
  ): Promise<void> => {
    return updateFlagConfig({ [key]: value });
  };

  // Persist one preference in the right schema so components stay schema-unaware.
  const setOptInPreference = async (
    preference: OptInPreference,
    optedIn: boolean
  ): Promise<void> => {
    // read at call-time to avoid a stale render closure
    const { optInPreferences } = selectFlagsConfig(store.getState());
    const payload: Partial<IFlagsConfig> = {};
    if (Array.isArray(optInPreferences)) {
      // clickStream is the master consent: turning it OFF clears all extras.
      const updatedPreferences =
        preference === OPT_IN_PREFERENCE.CLICK_STREAM && !optedIn
          ? []
          : applyPreference(optInPreferences, preference, optedIn);
      payload.optInPreferences = updatedPreferences;
      payload.isPrivacyAgreed = updatedPreferences.includes(
        OPT_IN_PREFERENCE.CLICK_STREAM
      );
      if (preference === OPT_IN_PREFERENCE.CLICK_STREAM) {
        payload.mandatoryButtonClicked = true;
      }
    } else if (preference === OPT_IN_PREFERENCE.CLICK_STREAM) {
      // legacy: only clickStream maps to the boolean
      payload.isPrivacyAgreed = optedIn;
      payload.mandatoryButtonClicked = true;
    } else {
      // non-clickStream pre-migration: nothing to write
      return;
    }
    return updateFlagConfig(payload);
  };

  const browsingDataOnlyPreferences = (): OptInPreference[] =>
    isClickStreamOptedIn(selectFlagsConfig(store.getState()))
      ? [OPT_IN_PREFERENCE.CLICK_STREAM]
      : [];

  const setOnboardingConsent = async (optedIn: boolean): Promise<void> => {
    const optInPreferences = optedIn
      ? [...OPT_IN_PREFERENCES]
      : browsingDataOnlyPreferences();
    return updateFlagConfig({
      optInPreferences,
      isPrivacyAgreed: optInPreferences.includes(
        OPT_IN_PREFERENCE.CLICK_STREAM
      ),
      eulaAgreed: true,
      mandatoryButtonClicked: true,
    });
  };

  const setExtendedDataOptedOut = async (): Promise<void> => {
    const { optInPreferences } = selectFlagsConfig(store.getState());
    if (!Array.isArray(optInPreferences)) {
      return;
    }
    const next = browsingDataOnlyPreferences();
    return updateFlagConfig({
      optInPreferences: next,
      isPrivacyAgreed: next.includes(OPT_IN_PREFERENCE.CLICK_STREAM),
    });
  };

  // Persists what the user ticked in the consent modal. Only ever adds buckets;
  // setTogglePreferences below is the only thing that takes one away. Takes the
  // whole variant rather than a second checkbox array so the two arguments can't
  // be passed in the wrong order and write someone else's consent. It does not
  // guard against legacy (pre-buckets) users the way setTogglePreferences does,
  // because the modal is never rendered for them; the render decides, not this.
  // Returns the buckets this write actually added (next minus existing), so the
  // caller can report exactly what changed instead of re-deriving the grants.
  // silent skips the full-page loader.
  const setConsent = async (
    variant: IConsentVariant,
    checked: ConsentCheckboxId[],
    silent = false
  ): Promise<OptInPreference[]> => {
    // read at call-time to avoid a stale render closure
    const flags = selectFlagsConfig(store.getState());
    const { optInPreferences } = flags;
    const existing = Array.isArray(optInPreferences) ? optInPreferences : [];
    const next = addGrantsToStoredBuckets(
      existing,
      variant.checkboxes,
      checkboxesCountedAsAccepted(variant, checked, flags)
    );
    const addedBuckets = next.filter(
      preference => !existing.includes(preference)
    );
    if (isMissingClickstreamConsent(next)) {
      // Reported rather than blocked on purpose: this only happens if the
      // GrowthBook targeting is wrong, and refusing the write would leave the
      // user stuck on the modal with no way forward.
      logger.error(CONSENT_WRITE_INVALID, undefined, { extra: { next } });
    }
    // Only a checkbox this variant actually offers can record terms acceptance,
    // for the same reason the grants above are filtered: a stale id must not write.
    const acceptedTerms = checked
      .filter(checkboxId => variant.checkboxes.includes(checkboxId))
      .some(checkboxId => CONSENT_CHECKBOXES[checkboxId].isTerms);
    const payload: Partial<IFlagsConfig> = {
      optInPreferences: next,
      isPrivacyAgreed: next.includes(OPT_IN_PREFERENCE.CLICK_STREAM),
      mandatoryButtonClicked: true,
      ...newlyAcceptedDisclosureFlags(variant, checked, flags),
    };
    if (acceptedTerms) {
      payload.eulaAgreed = true;
    }
    await (silent ? updateFlagConfigSilently : updateFlagConfig)(payload);
    return addedBuckets;
  };

  // Settings toggles resolve the target preference set themselves and write it.
  const setTogglePreferences = async (
    next: OptInPreference[]
  ): Promise<void> => {
    const { optInPreferences } = selectFlagsConfig(store.getState());
    // Legacy schema: never fabricate the array — that would silently migrate the user.
    if (!Array.isArray(optInPreferences)) {
      return updateFlagConfig({
        isPrivacyAgreed: next.includes(OPT_IN_PREFERENCE.CLICK_STREAM),
        mandatoryButtonClicked: true,
      });
    }
    const normalizedNext = normalizePreferences(next);
    if (isMissingClickstreamConsent(normalizedNext)) {
      logger.error(CONSENT_WRITE_INVALID, undefined, {
        extra: { normalizedNext },
      });
    }
    return updateFlagConfig({
      optInPreferences: normalizedNext,
      isPrivacyAgreed: normalizedNext.includes(OPT_IN_PREFERENCE.CLICK_STREAM),
      mandatoryButtonClicked: true,
    });
  };

  // Write-once marker that the opt-in screen was seen (single, swappable call site).
  const markBucketsOptInScreenSeen = async (): Promise<void> => {
    const { sawBucketsOptInOnboardingScreen } = selectFlagsConfig(
      store.getState()
    );
    if (sawBucketsOptInOnboardingScreen) {
      return;
    }
    return updateFlagConfigSilently({ sawBucketsOptInOnboardingScreen: true });
  };

  // Permanently dismiss the re-consent banner. Silent write; no data-collection effect.
  const dismissReconsentBanner = async (): Promise<void> => {
    const { reconsentBannerDismissed } = selectFlagsConfig(store.getState());
    if (reconsentBannerDismissed) {
      return;
    }
    return updateFlagConfigSilently({ reconsentBannerDismissed: true });
  };

  // Terms-updated "Got it": persist EULA and grant all buckets except AI, then
  // return the granted buckets so the caller can fire analytics from the click site.
  // Always send an optInPreferences array on the new schema — Redux often has
  // onNewOptInSchema without the array (extension bridge strips optInPreferences),
  // and isPrivacyAgreed alone does not populate the extension's bucket store.
  const acceptTermsUpdated = async (): Promise<OptInPreference[]> => {
    const { optInPreferences, onNewOptInSchema } = selectFlagsConfig(
      store.getState()
    );
    const payload: Partial<IFlagsConfig> = {
      eulaAgreed: true,
      mandatoryButtonClicked: true,
    };
    const granted: OptInPreference[] = [];

    if (onNewOptInSchema === true || Array.isArray(optInPreferences)) {
      let updatedPreferences = Array.isArray(optInPreferences)
        ? optInPreferences
        : [];
      for (const preference of TERMS_UPDATED_CONSENT_BUCKETS) {
        updatedPreferences = applyPreference(
          updatedPreferences,
          preference,
          true
        );
      }
      payload.optInPreferences = updatedPreferences;
      payload.isPrivacyAgreed = true;
      granted.push(...TERMS_UPDATED_CONSENT_BUCKETS);
    } else {
      payload.isPrivacyAgreed = true;
    }

    try {
      await updateFlagConfigSilently(payload);
      return granted;
    } catch (error) {
      logger.error('acceptTermsUpdated failed', error);
      return [];
    }
  };

  return {
    updateFlagConfig,
    updateFlagConfigSilently,
    updateSingleFlag,
    setOptInPreference,
    setOnboardingConsent,
    setExtendedDataOptedOut,
    setConsent,
    setTogglePreferences,
    markBucketsOptInScreenSeen,
    dismissReconsentBanner,
    acceptTermsUpdated,
  };
};
